// ── 資料庫查詢 API（唯讀資料） ────────────────────────────────────────────────

import { apiFetch, API_BASE_URL } from './client';
import type { Ratio, Style, Toolkit, Tone, HistoryItem, TransferStyle } from '../../types';
import type { FlowModel } from '../../types/flowModel';

export const fetchRatios = async (): Promise<Ratio[]> => {
  const res = await apiFetch(`${API_BASE_URL}/db/ratios`);
  if (!res.ok) throw new Error('Failed to fetch ratios');
  return res.json();
};

export const fetchStyles = async (): Promise<Style[]> => {
  const res = await apiFetch(`${API_BASE_URL}/db/styles`);
  if (!res.ok) throw new Error('Failed to fetch styles');
  return res.json();
};

export const fetchToolkits = async (): Promise<Toolkit[]> => {
  const res = await apiFetch(`${API_BASE_URL}/db/toolkits`);
  if (!res.ok) throw new Error('Failed to fetch toolkits');
  return res.json();
};

export const fetchTones = async (): Promise<Tone[]> => {
  const res = await apiFetch(`${API_BASE_URL}/db/tones`);
  if (!res.ok) throw new Error('Failed to fetch tones');
  return res.json();
};

export const fetchFlowModels = async (): Promise<FlowModel[]> => {
  const res = await apiFetch(`${API_BASE_URL}/db/flow-models`);
  if (!res.ok) throw new Error('Failed to fetch flow models');
  return res.json();
};

export const fetchToolkitCategories = async (): Promise<string[]> => {
  const res = await apiFetch(`${API_BASE_URL}/db/toolkit-categories`);
  if (!res.ok) throw new Error('Failed to fetch toolkit categories');
  const data: { name: string }[] = await res.json();
  return data.map(c => c.name);
};

export const fetchTransferStyles = async (): Promise<TransferStyle[]> => {
  const res = await apiFetch(`${API_BASE_URL}/db/transfer-styles`);
  if (!res.ok) throw new Error('Failed to fetch transfer styles');
  return res.json();
};

// ── Gallery APIs ──────────────────────────────────────────────────────────────

export interface GalleryItem {
  id: string;
  source: string;
  imageUrl: string;
  /** 列表縮圖（後端代理）。缺失時 fallback 用 imageUrl */
  imageThumbnailUrl?: string;
  gcsPath: string;
  mimeType: string;
  originalName: string;
  /** 生成這張圖片的流程紀錄 id。無值代表不支援「繼續生成」（功能上線前產生的舊圖）。 */
  generationSessionId?: string;
  createdAt: string;
}

export interface GalleryResponse {
  data: GalleryItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const fetchGallery = async (page = 1, limit = 24): Promise<GalleryResponse> => {
  const res = await apiFetch(`${API_BASE_URL}/db/gallery?page=${page}&limit=${limit}`);
  if (!res.ok) throw new Error('Failed to fetch gallery');
  return res.json();
};

export const deleteGalleryItems = async (ids: string[]): Promise<{ deleted: number }> => {
  const res = await apiFetch(`${API_BASE_URL}/db/gallery`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error('Failed to delete gallery items');
  return res.json();
};

export const saveStampedImage = async (blob: Blob): Promise<GalleryItem> => {
  const form = new FormData();
  form.append('image', blob, `stamp-edit-${Date.now()}.jpg`);
  const res = await apiFetch(`${API_BASE_URL}/db/gallery/save-stamped`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error('Failed to save stamped image');
  return res.json();
};

// ── History APIs ──────────────────────────────────────────────────────────────

export interface HistoryResponse {
  data: HistoryItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number; };
}

export const fetchHistory = async (page = 1, limit = 20): Promise<HistoryResponse> => {
  const res = await apiFetch(`${API_BASE_URL}/db/history?page=${page}&limit=${limit}`);
  if (!res.ok) throw new Error('Failed to fetch history');
  return res.json();
};
